import React from 'react';
import { motion } from 'framer-motion';
import { FaCogs, FaIndustry, FaRobot, FaCheckCircle, FaChevronDown, FaExclamationTriangle } from 'react-icons/fa';

const AnimatedSection = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

const Header = () => (
  <header className="relative min-h-screen flex items-center justify-center overflow-hidden bg-cover bg-center" style={{ backgroundImage: "url('/images/circuit-pattern.png')" }}>
    <div className="absolute inset-0 bg-gradient-to-br from-blue-900 to-indigo-900 opacity-75"></div>
    <div className="container mx-auto px-4 text-center relative z-10">
      <motion.h1 
        className="text-4xl sm:text-5xl md:text-7xl lg:text-9xl font-bold mb-4 sm:mb-8 text-white drop-shadow-lg"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.2 }}
      >
        ARUMCODE
      </motion.h1>
      <motion.p 
        className="text-lg sm:text-xl md:text-2xl lg:text-4xl mb-6 sm:mb-12 text-blue-200 drop-shadow-md"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.4 }}
      >
        アルム株式会社が提供する、AI搭載の製造業向け革新的ソリューション
      </motion.p>
      <motion.p 
        className="text-base sm:text-lg md:text-xl lg:text-2xl mb-8 sm:mb-12 text-blue-100 drop-shadow-md"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.6 }}
      >
        製造業のデジタル変革を実現する次世代ERPシステム
      </motion.p>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.8 }}
      >
        <a
          href="/contact"
          className="inline-block bg-white text-indigo-900 py-3 px-6 sm:py-4 sm:px-8 rounded-full text-lg sm:text-xl font-semibold hover:bg-indigo-100 transition duration-300 shadow-lg"
        >
          詳細資料請求
        </a>
      </motion.div>
      <motion.div
        className="mt-8 sm:mt-12"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1 }}
      >
        <a href="https://arumcode.com/" target="_blank" rel="noopener noreferrer">
          <img src="/images/arum_company.jpg" alt="アルム株式会社" className="mx-auto rounded-lg shadow-lg max-w-full h-auto" style={{ maxWidth: '300px' }} />
        </a>
      </motion.div>
    </div>
    <motion.div
      className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
      animate={{ y: [0, 10, 0] }}
      transition={{ duration: 1.5, repeat: Infinity }}
    >
      <FaChevronDown className="text-3xl sm:text-4xl text-white" />
    </motion.div>
  </header>
);

const ProductIntroduction = () => (
  <section className="py-16 sm:py-24 bg-gray-50">
    <div className="container mx-auto px-4">
      <AnimatedSection>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8 sm:mb-12 text-center text-gray-800">ARUMCODEのご紹介</h2>
        <p className="text-base sm:text-lg md:text-xl text-center mb-12 sm:mb-16 text-gray-600 max-w-4xl mx-auto">
          ARUMCODEは、AI技術を活用した製造業向けの革新的なクラウドERPソリューションです。
          図面データの解析から加工プロセスの生成、さらには見積作成まで、製造業の核心的な業務をサポートし、
          生産性の飛躍的な向上と人材不足の解消を同時に実現します。
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
          <motion.div className="text-center bg-white p-6 sm:p-8 rounded-xl shadow-lg transition-all duration-300 hover:shadow-2xl"
            whileHover={{ scale: 1.05 }}
          >
            <FaRobot className="text-4xl sm:text-6xl text-indigo-600 mx-auto mb-4 sm:mb-6" />
            <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">AI活用</h3>
            <p className="text-sm sm:text-base text-gray-600">図面解析から見積作成までAIがサポート</p>
          </motion.div>
          <motion.div className="text-center bg-white p-6 sm:p-8 rounded-xl shadow-lg transition-all duration-300 hover:shadow-2xl"
            whileHover={{ scale: 1.05 }}
          >
            <FaIndustry className="text-4xl sm:text-6xl text-indigo-600 mx-auto mb-4 sm:mb-6" />
            <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">製造業に特化</h3>
            <p className="text-sm sm:text-base text-gray-600">金属加工業界のニーズに最適化</p>
          </motion.div>
          <motion.div className="text-center bg-white p-6 sm:p-8 rounded-xl shadow-lg transition-all duration-300 hover:shadow-2xl"
            whileHover={{ scale: 1.05 }}
          >
            <FaCogs className="text-4xl sm:text-6xl text-indigo-600 mx-auto mb-4 sm:mb-6" />
            <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">柔軟なカスタマイズ</h3>
            <p className="text-sm sm:text-base text-gray-600">お客様の業務に合わせて調整可能</p>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  </section>
);

const Feature = ({ icon, title, description }) => (
  <motion.div
    className="bg-white rounded-xl shadow-lg p-6 sm:p-8 transition-all duration-300 hover:shadow-2xl"
    whileHover={{ scale: 1.05 }}
  >
    <div className="text-3xl sm:text-5xl text-indigo-600 mb-4 sm:mb-6">{icon}</div>
    <h3 className="text-lg sm:text-2xl font-semibold mb-3 sm:mb-4">{title}</h3>
    <p className="text-sm sm:text-base text-gray-600">{description}</p>
  </motion.div>
);

const Features = () => (
  <section className="py-16 sm:py-24 bg-gray-100">
    <div className="container mx-auto px-4">
      <AnimatedSection>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-12 sm:mb-16 text-center text-gray-800">ARUMCODEの主な特徴</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
          <Feature 
            icon={<FaCogs />}
            title="AI図面解析" 
            description="AIが図面データを自動解析し、必要な情報を抽出。複雑な図面も瞬時に理解し、製造プロセスの基礎となるデータを生成します。" 
          />
          <Feature 
            icon={<FaCogs />}
            title="加工プロセス自動生成" 
            description="抽出されたデータを基に、AIが最適な加工プロセスを自動生成。効率的な製造手順を提案し、生産性を大幅に向上させます。" 
          />
          <Feature 
            icon={<FaCogs />}
            title="AI支援見積作成" 
            description="生成された加工プロセスに基づき、AIが精密な見積を作成支援。人的エラーを削減し、迅速かつ正確な見積提供を実現します。" 
          />
          <Feature 
            icon={<FaCogs />}
            title="統合品質管理" 
            description="製造プロセス全体での品質管理を実現。不良品の削減と顧客満足度の向上につながります。" 
          />
          <Feature 
            icon={<FaRobot />}
            title="自動化による人材不足対策" 
            description="AIによる業務の自動化と効率化により、少ない人員でも高い生産性を維持。人材不足の課題に対応します。" 
          />
          <Feature 
            icon={<FaCogs />}
            title="柔軟なカスタマイズ" 
            description="お客様の特定のニーズに合わせて機能をカスタマイズ。独自の製造プロセスに最適化します。" 
          />
        </div>
      </AnimatedSection>
    </div>
  </section>
);

const ChallengeCard = ({ icon, title, description, solution }) => (
  <motion.div
    className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl"
    whileHover={{ scale: 1.05 }}
  >
    <div className="bg-red-50 p-6 sm:p-8">
      <div className="flex items-center mb-4 sm:mb-6">
        {icon}
        <h3 className="text-lg sm:text-2xl font-semibold ml-4">{title}</h3>
      </div>
      <p className="text-sm sm:text-base text-gray-700">{description}</p>
    </div>
    <div className="bg-green-50 p-6 sm:p-8">
      <h4 className="text-base sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center">
        <FaCheckCircle className="text-green-500 mr-3" />
        ARUMCODEによる解決策
      </h4>
      <p className="text-sm sm:text-base text-gray-700">{solution}</p>
    </div>
  </motion.div>
);

const Challenges = () => (
  <section className="py-16 sm:py-24">
    <div className="container mx-auto px-4">
      <AnimatedSection>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-12 sm:mb-16 text-center text-gray-800">製造業の課題とARUMCODEによる解決</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
          <ChallengeCard 
            icon={<FaExclamationTriangle className="text-3xl sm:text-5xl text-red-500" />}
            title="複雑な図面解析"
            description="複雑な図面の解析に時間がかかり、エラーのリスクも高くなっています。"
            solution="AIが瞬時に図面を解析し、必要なデータを抽出。人的エラーを削減し、処理時間を大幅に短縮します。"
          />
          <ChallengeCard 
            icon={<FaExclamationTriangle className="text-3xl sm:text-5xl text-red-500" />}
            title="非効率な加工プロセス設計"
            description="加工プロセスの設計に多くの時間と経験が必要で、最適化が困難です。"
            solution="AIが自動的に最適な加工プロセスを生成。効率的な製造手順を提案し、生産性を向上させます。"
          />
          <ChallengeCard 
            icon={<FaExclamationTriangle className="text-3xl sm:text-5xl text-red-500" />}
            title="見積作成の遅延と不正確さ"
            description="見積作成に時間がかかり、人的エラーによる不正確さが問題となっています。"
            solution="AIが精密な見積を自動生成。迅速かつ正確な見積提供により、ビジネスチャンスの損失を防ぎます。"
          />
        </div>
      </AnimatedSection>
    </div>
  </section>
);

const BenefitCard = ({ title, description }) => (
  <motion.div
    className="bg-white rounded-xl shadow-lg p-6 sm:p-8 transition-all duration-300 hover:shadow-2xl"
    whileHover={{ scale: 1.05 }}
  >
    <h3 className="text-lg sm:text-2xl font-semibold mb-3 sm:mb-4">{title}</h3>
    <p className="text-sm sm:text-base text-gray-600">{description}</p>
  </motion.div>
);

const Benefits = () => (
  <section className="py-16 sm:py-24 bg-gray-50">
    <div className="container mx-auto px-4">
      <AnimatedSection>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-12 sm:mb-16 text-center text-gray-800">ARUMCODE導入のメリット</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 mb-12 sm:mb-16">
          <BenefitCard 
            title="生産性の大幅向上" 
            description="AIによる業務プロセスの最適化により、生産性が飛躍的に向上。人手不足の解消に大きく貢献します。"
          />
          <BenefitCard 
            title="見積精度の向上" 
            description="AI支援による高精度な見積作成で、適正な価格設定と利益確保を実現します。"
          />
          <BenefitCard 
            title="リードタイムの短縮" 
            description="図面解析から見積作成までの時間を大幅に削減し、顧客対応のスピードアップを実現します。"
          />
          <BenefitCard 
            title="人的エラーの削減" 
            description="AIによる自動化で、図面解析や加工プロセス設計における人的エラーを最小限に抑えます。"
          />
          <BenefitCard 
            title="データに基づく経営判断" 
            description="生産データの可視化と分析により、より正確な経営判断と戦略立案が可能になります。"
          />
          <BenefitCard 
            title="競争力の強化" 
            description="効率化とコスト削減により、市場での競争力が大幅に強化されます。"
          />
        </div>
        <motion.div 
          className="mt-12 sm:mt-16 bg-indigo-900 p-8 sm:p-12 rounded-2xl shadow-2xl"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
        >
          <img 
            src="/images/almcode-benefits-diagram.jpg" 
            alt="ARUMCODE導入のメリット図解" 
            className="mx-auto rounded-xl shadow-lg max-w-full h-auto"
          />
        </motion.div>
      </AnimatedSection>
    </div>
  </section>
);

const ProductScreenshots = () => (
  <section className="py-16 sm:py-24 bg-gray-100">
    <div className="container mx-auto px-4">
      <AnimatedSection>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-12 sm:mb-16 text-center text-gray-800">ARUMCODEのインターフェース</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
          <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }}>
            <img src="/images/almcode-ai-analysis.jpg" alt="ARUMCODE AI図面解析画面" className="rounded-xl shadow-lg w-full" />
            <p className="mt-4 sm:mt-6 text-center text-base sm:text-xl text-gray-600">AI搭載の図面解析インターフェース</p>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }}>
            <img src="/images/almcode-process-generation.jpg" alt="ARUMCODE 加工プロセス生成画面" className="rounded-xl shadow-lg w-full" />
            <p className="mt-4 sm:mt-6 text-center text-base sm:text-xl text-gray-600">AIによる加工プロセス自動生成機能</p>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  </section>
);

const VideoPlayer = ({ src, title }) => (
  <div className="w-full">
    <div className="relative pb-[56.25%] h-0">
      <iframe
        src={src}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute top-0 left-0 w-full h-full rounded-lg shadow-md"
      ></iframe>
    </div>
  </div>
);

const ProductVideos = () => (
  <section className="py-16 sm:py-24 bg-gray-50">
    <div className="container mx-auto px-4">
      <AnimatedSection>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-12 sm:mb-16 text-center text-gray-800">
          製品紹介動画
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <VideoPlayer 
            src="https://www.youtube.com/embed/0tB-es3tY4w"
            title="アルムコード紹介動画1"
          />
          <VideoPlayer 
            src="https://www.youtube.com/embed/XJTatjsMM8I"
            title="アルムコード紹介動画2"
          />
          <VideoPlayer 
            src="https://www.youtube.com/embed/S7oRRpYkIuU"
            title="アルムコード紹介動画3"
          />
        </div>
      </AnimatedSection>
    </div>
  </section>
);

const CaseStudy = () => (
  <section className="py-16 sm:py-24">
    <div className="container mx-auto px-4">
      <AnimatedSection>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-12 sm:mb-16 text-center text-gray-800">導入事例</h2>
        <motion.div 
          className="bg-white rounded-xl shadow-2xl p-8 sm:p-12"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-2xl sm:text-3xl font-semibold mb-4 sm:mb-6">A社様（金属加工業）</h3>
          <p className="text-base sm:text-xl mb-6 sm:mb-8">従業員50名の金属加工会社。ARUMCODE導入後、以下の成果を達成：</p>
          <ul className="list-disc list-inside mb-6 sm:mb-8 text-base sm:text-xl space-y-2 sm:space-y-4">
            <li>見積作成時間が75%短縮</li>
            <li>見積精度が15%向上</li>
            <li>生産計画の立案時間が50%削減</li>
            <li>overall生産性が30%向上</li>
          </ul>
          <p className="text-base sm:text-xl italic">「ARUMCODEの導入により、図面解析から見積作成、生産計画立案までの一連のプロセスが大幅に効率化されました。特に、AIによる図面解析と加工プロセス生成機能は、我々の業務改革の大きな推進力となっています。」 - A社 生産管理部長</p>
        </motion.div>
      </AnimatedSection>
    </div>
  </section>
);

const ContactLink = () => (
  <section className="py-16 sm:py-24 bg-gradient-to-r from-blue-800 to-indigo-900 text-white">
    <div className="container mx-auto px-4 text-center">
      <AnimatedSection>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8">ARUMCODEで製造業の未来を切り開く</h2>
        <p className="text-lg sm:text-xl md:text-2xl mb-8 sm:mb-12">当社は、ARUMCODEの導入支援から運用サポートまで、お客様の製造プロセス改革をサポートいたします。詳細や個別相談は、お問い合わせページよりお気軽にご連絡ください。</p>
        <motion.a
          href="/contact"
          className="inline-block bg-white text-indigo-900 py-3 px-8 sm:py-4 sm:px-10 rounded-full text-lg sm:text-xl font-semibold hover:bg-indigo-100 transition duration-300 shadow-lg"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          お問い合わせ・資料請求はこちら
        </motion.a>
      </AnimatedSection>
    </div>
  </section>
);

const ARUMCODELandingPage = () => {
  return (
    <div className="font-sans bg-gray-50">
      <Header />
      <ProductIntroduction />
      <ProductScreenshots />
      <Features />
      <Challenges />
      <Benefits />
      <ProductVideos />
      <CaseStudy />
      <ContactLink />
    </div>
  );
};

export default ARUMCODELandingPage;